/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

class RoofCalculator {
  static get EDGE_POSITIONS() {
    return ['top', 'right', 'bottom', 'left'];
  }

  static get EDGE_LABELS() {
    return {
      top: 'A',
      right: 'B',
      bottom: 'C',
      left: 'D'
    };
  }

  static parseJson(text) {
    try {
      return JSON.parse(text);
    } catch (error) {
      return null;
    }
  }

  static parseNumber(value) {
    if (value === null || value === void 0 || value === '') {
      return null;
    }

    const normalizedValue = String(value).replace(',', '.').trim();
    const numberMatch = normalizedValue.match(/-?\d+(?:\.\d+)?/);
    const parsedValue = Number(numberMatch ? numberMatch[0] : normalizedValue);

    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  static positiveNumber(value) {
    const parsedValue = this.parseNumber(value);
    return parsedValue && parsedValue > 0 ? parsedValue : null;
  }

  static roundUp(value, increment) {
    if (!increment || increment <= 0) {
      return value;
    }

    return Math.ceil(value / increment) * increment;
  }

  static formatMeters(value) {
    return `${this.trimTrailingZeros(value)}m`;
  }

  static formatSquareMeters(value) {
    return `${this.trimTrailingZeros(value)}m2`;
  }

  static trimTrailingZeros(value) {
    if (!Number.isFinite(value)) {
      return '';
    }

    return value.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
  }

  static normalizeText(value) {
    return String(value || '').toLowerCase();
  }

  static arrayify(value) {
    return Array.isArray(value) ? value : [];
  }

  static absoluteUrl(url) {
    if (!url) {
      return '';
    }

    return String(url).replace(/^\/\//, `${window.location.protocol}//`);
  }

  static uniqueNumbers(values) {
    return [...new Set(this.arrayify(values).map((value) => this.positiveNumber(value)).filter(Boolean))].sort((left, right) => left - right);
  }

  static formatMoneyValue(cents, format) {
    const normalizedCents = Number(cents);
    const settings = window.themeVariables && window.themeVariables.settings ? window.themeVariables.settings : {};
    const formatString = format || (
      settings.currencyCodeEnabled
        ? settings.moneyWithCurrencyFormat
        : settings.moneyFormat
    ) || '${{amount}}';

    if (!Number.isFinite(normalizedCents)) {
      return '';
    }

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const placeholderMatch = formatString.match(placeholderRegex);
    const placeholder = placeholderMatch ? placeholderMatch[1] : 'amount';

    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      const formattedNumber = (number / 100).toFixed(precision);
      const parts = formattedNumber.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const fraction = parts[1] ? `${decimal}${parts[1]}` : '';

      return `${whole}${fraction}`;
    }

    let value = '';

    switch (placeholder) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(normalizedCents, 0);
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(normalizedCents, 2, ' ', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(normalizedCents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(normalizedCents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(normalizedCents, 0, ' ', '.');
        break;
      default:
        value = formatWithDelimiters(normalizedCents, 2);
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  static productKey(product) {
    return String(product.id || product.handle || product.url || product.title);
  }

  static normalizeProductRef(product, reason, overrides = {}) {
    if (!product) {
      return null;
    }

    if (product.product) {
      return {
        id: product.id || product.product.id,
        title: overrides.title || [product.product.title, product.title].filter(Boolean).join(' - '),
        url: product.product.url,
        image: product.product.image || product.image || null,
        variantId: product.variantId || product.product.variantId || null,
        price: Number(product.price ?? product.product.price ?? 0),
        compareAtPrice: Number(product.compareAtPrice ?? product.product.compareAtPrice ?? 0),
        available: typeof product.available === 'boolean' ? product.available : product.product.available,
        reason,
        quantity: Math.max(1, Math.ceil(overrides.quantity || product.quantity || 1))
      };
    }

    return {
      id: product.id,
      title: overrides.title || product.title,
      url: product.url,
      image: product.image || null,
      variantId: product.variantId || null,
      price: Number(product.price || 0),
      compareAtPrice: Number(product.compareAtPrice || 0),
      available: typeof product.available === 'boolean' ? product.available : true,
      reason,
      quantity: Math.max(1, Math.ceil(overrides.quantity || product.quantity || 1))
    };
  }

  static addProductRecommendation(map, product, reason, overrides) {
    const normalizedProduct = this.normalizeProductRef(product, reason, overrides);

    if (!normalizedProduct) {
      return;
    }

    const key = this.productKey(normalizedProduct);

    if (!map.has(key)) {
      map.set(key, normalizedProduct);
    }
  }

  static findFirstWidthAtLeast(widths, targetWidth) {
    return widths.find((width) => width >= targetWidth) || widths[widths.length - 1] || targetWidth;
  }

  static parseWidthFromTitle(title) {
    const match = String(title || '').match(/(\d+(?:[.,]\d+)?)\s*m\b/i);

    if (!match) {
      return null;
    }

    return this.parseNumber(match[1]);
  }

  static widthMatches(targetWidth, candidateWidth, tolerance = 0.1) {
    if (!Number.isFinite(targetWidth) || !Number.isFinite(candidateWidth)) {
      return false;
    }

    return Math.abs(targetWidth - candidateWidth) <= tolerance;
  }

  static buildAdhesiveOptions(optionList, fallbackProduct) {
    const list = this.arrayify(optionList);
    return list.length ? list : (fallbackProduct ? [fallbackProduct] : []);
  }

  static selectAdhesivePackCombination(options, requiredVolume) {
    const packs = this.arrayify(options)
      .map((option) => ({
        product: option,
        volume: this.positiveNumber(option.volumeL) || this.parseVolumeFromTitle(option.title),
        cost: this.positiveNumber(option.price) || 0
      }))
      .filter((pack) => pack.volume);

    if (!packs.length) {
      return null;
    }

    const needed = Math.max(0, Number.isFinite(requiredVolume) ? requiredVolume : 0);
    const units = Math.max(1, Math.ceil(needed * 100));
    const priceAware = packs.some((pack) => pack.cost > 0);

    const bestCost = new Array(units + 1).fill(Infinity);
    const choice = new Array(units + 1).fill(-1);
    bestCost[0] = 0;

    for (let amount = 1; amount <= units; amount += 1) {
      packs.forEach((pack, index) => {
        const packUnits = Math.round(pack.volume * 100);
        const remaining = Math.max(0, amount - packUnits);
        const packCost = priceAware ? pack.cost : pack.volume;
        const candidateCost = bestCost[remaining] + packCost;

        if (candidateCost < bestCost[amount]) {
          bestCost[amount] = candidateCost;
          choice[amount] = index;
        }
      });
    }

    const combination = new Map();
    let remainingUnits = units;

    while (remainingUnits > 0) {
      const index = choice[remainingUnits];

      if (index === -1) {
        return null;
      }

      combination.set(index, (combination.get(index) || 0) + 1);
      remainingUnits = Math.max(0, remainingUnits - Math.round(packs[index].volume * 100));
    }

    return [...combination.entries()].map(([index, quantity]) => ({ pack: packs[index], quantity }));
  }

  static parseProductDimensions(title) {
    const match = String(title || '').match(/(\d+(?:[.,]\d+)?)\s*m?\s*(?:x|×)\s*(\d+(?:[.,]\d+)?)\s*m?/i);

    if (!match) {
      return null;
    }

    const first = this.parseNumber(match[1]);
    const second = this.parseNumber(match[2]);

    if (!first || !second) {
      return null;
    }

    return { first, second };
  }

  static selectMembraneVariant(product, requiredLength) {
    const candidates = this.arrayify(product && product.variants)
      .map((variant) => ({
        variant,
        length: this.parseWidthFromTitle(variant.title)
      }))
      .filter((entry) => entry.length);

    if (!candidates.length) {
      return null;
    }

    const longEnough = candidates
      .filter((entry) => entry.length >= requiredLength)
      .sort((a, b) => a.length - b.length);

    if (longEnough.length) {
      return longEnough[0];
    }

    return candidates.sort((a, b) => b.length - a.length)[0];
  }

  static findMembraneByRollWidth(products, rollWidth, requiredLength) {
    let match = null;
    const targetWidth = this.positiveNumber(rollWidth);

    if (!targetWidth) {
      return null;
    }

    this.arrayify(products).forEach((product) => {
      if (match) {
        return;
      }

      const dimensions = this.parseProductDimensions(product.title);

      if (dimensions) {
        const options = [
          { width: dimensions.first, length: dimensions.second },
          { width: dimensions.second, length: dimensions.first }
        ];

        options.forEach((option) => {
          if (match) {
            return;
          }

          if (this.widthMatches(targetWidth, option.width) && option.length >= requiredLength) {
            match = {
              product,
              width: option.width,
              length: option.length,
              waste: (option.width * option.length) - (targetWidth * requiredLength)
            };
          }
        });

        return;
      }

      const widthOnly = this.parseWidthFromTitle(product.title);

      if (this.widthMatches(targetWidth, widthOnly)) {
        const variantMatch = this.selectMembraneVariant(product, requiredLength);

        match = {
          product,
          width: targetWidth,
          length: variantMatch ? variantMatch.length : requiredLength,
          variant: variantMatch ? variantMatch.variant : null,
          waste: 0
        };
      }
    });

    return match;
  }

  static findBestMembraneProduct(products, requiredWidth, requiredLength) {
    let bestMatch = null;

    this.arrayify(products).forEach((product) => {
      const dimensions = this.parseProductDimensions(product.title);

      if (dimensions) {
        const options = [
          { width: dimensions.first, length: dimensions.second },
          { width: dimensions.second, length: dimensions.first }
        ];

        options.forEach((option) => {
          if (!this.widthMatches(requiredWidth, option.width) || option.length < requiredLength) {
            return;
          }

          const waste = (option.width * option.length) - (requiredWidth * requiredLength);

          if (!bestMatch || waste < bestMatch.waste) {
            bestMatch = {
              product,
              width: option.width,
              length: option.length,
              waste
            };
          }
        });

        return;
      }

      const widthOnly = this.parseWidthFromTitle(product.title);

      if (!widthOnly || !this.widthMatches(requiredWidth, widthOnly)) {
        return;
      }

      const waste = widthOnly - requiredWidth;

      if (!bestMatch || waste < bestMatch.waste) {
        const variantMatch = this.selectMembraneVariant(product, requiredLength);

        bestMatch = {
          product,
          width: widthOnly,
          length: variantMatch ? variantMatch.length : requiredLength,
          variant: variantMatch ? variantMatch.variant : null,
          waste
        };
      }
    });

    return bestMatch;
  }

  static resolveExactMembrane(products, rollWidths, maxSingleWidth, maximumSheetLength, dimensions) {
    const orientations = [
      { width: dimensions.width + dimensions.overhang, length: dimensions.length + dimensions.overhang },
      { width: dimensions.length + dimensions.overhang, length: dimensions.width + dimensions.overhang }
    ];

    let best = null;

    orientations.forEach((orientation) => {
      if (maximumSheetLength && orientation.length > maximumSheetLength) {
        return;
      }

      if (maxSingleWidth && orientation.width > maxSingleWidth) {
        return;
      }

      const rollWidth = this.findFirstWidthAtLeast(rollWidths, orientation.width);
      const match = this.findMembraneByRollWidth(products, rollWidth, orientation.length)
        || this.findBestMembraneProduct(products, rollWidth, orientation.length);

      if (!match) {
        return;
      }

      const waste = (match.width * match.length) - (orientation.width * orientation.length);

      if (!best || waste < best.waste) {
        best = Object.assign({}, match, { waste });
      }
    });

    return best;
  }

  static resolveOverhang(profile) {
    const configured = this.positiveNumber(profile && profile.defaultOverhangM);

    return configured || 0.15;
  }

  static edgeLengthMeters(edge, finishedLength, finishedWidth) {
    return (edge === 'top' || edge === 'bottom') ? finishedWidth : finishedLength;
  }

  static getTrimProduct(products, edgeType) {
    if (edgeType === 'curb') {
      return products.curbTrim || null;
    }

    if (edgeType === 'drip') {
      return products.dripTrim || null;
    }

    return null;
  }

  static edgeTypeLabel(edgeType) {
    if (edgeType === 'drip') {
      return 'Drip trim';
    }

    if (edgeType === 'curb') {
      return 'Curb trim';
    }

    return 'No trim';
  }

  static normalizeEdgeSelections(edgeSelections) {
    return this.EDGE_POSITIONS.reduce((result, edge) => {
      const value = this.normalizeText(edgeSelections && edgeSelections[edge]);
      result[edge] = value || 'none';
      return result;
    }, {});
  }

  static buildEdgeSelectionSummary(edgeSelections) {
    const normalizedSelections = this.normalizeEdgeSelections(edgeSelections);

    return this.EDGE_POSITIONS
      .map((edge) => `${this.EDGE_LABELS[edge]}: ${this.edgeTypeLabel(normalizedSelections[edge])}`)
      .join('; ');
  }

  static edgeSelectionBorderColor(edgeType, activeColor) {
    if (edgeType === 'drip' || edgeType === 'curb') {
      return activeColor || 'transparent';
    }

    return 'transparent';
  }

  static edgeSelectionBorderStyle(edgeType) {
    if (edgeType === 'curb') {
      return 'solid';
    }

    if (edgeType === 'drip') {
      return 'double';
    }

    return 'solid';
  }

  static formatEdgeNames(edges) {
    const labels = this.arrayify(edges).map((edge) => this.EDGE_LABELS[edge] || edge);

    if (!labels.length) {
      return '';
    }

    if (labels.length === 1) {
      return labels[0];
    }

    if (labels.length === 2) {
      return `${labels[0]} and ${labels[1]}`;
    }

    return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
  }

  static findCoverageRule(rules, matcher, fallbackValue) {
    const rule = this.arrayify(rules).find((item) => matcher(this.normalizeText(item.name), this.normalizeText(item.productType), this.normalizeText(item.triggerCondition)));
    const coverageValue = this.positiveNumber(rule && rule.coverageValue) || fallbackValue;

    return {
      rule,
      coverageValue
    };
  }

  static buildQuantityLabel(quantity, suffix) {
    if (!Number.isFinite(quantity)) {
      return '';
    }

    return `${this.trimTrailingZeros(quantity)}${suffix}`;
  }

  static firstPositiveNumber(values) {
    return this.arrayify(values).map((value) => this.positiveNumber(value)).find(Boolean) || null;
  }

  static parsePackSizes(packSizes) {
    if (typeof packSizes === 'string') {
      return this.uniqueNumbers(packSizes.split(/[,\s/]+/));
    }

    if (Array.isArray(packSizes)) {
      return this.uniqueNumbers(packSizes);
    }

    if (packSizes && typeof packSizes === 'object') {
      return this.uniqueNumbers(Object.values(packSizes));
    }

    const parsed = this.positiveNumber(packSizes);
    return parsed ? [parsed] : [];
  }

  static parseVolumeFromTitle(title) {
    const match = String(title || '').match(/(\d+(?:[.,]\d+)?)\s*(?:l|litre|litres|liter|liters)\b/i);
    return match ? this.parseNumber(match[1]) : null;
  }

  static resolvePackQuantity(requiredAmount, product, rule) {
    const packSize = this.positiveNumber(product && product.volumeL) || this.parseVolumeFromTitle(product && product.title) || this.firstPositiveNumber(this.parsePackSizes(rule && rule.packSizes));

    if (!Number.isFinite(requiredAmount) || requiredAmount <= 0) {
      return 1;
    }

    if (!packSize) {
      return Math.max(1, Math.ceil(requiredAmount));
    }

    return Math.max(1, Math.ceil(requiredAmount / packSize));
  }

  static createCartItem(product, quantity, properties) {
    if (!product || !product.variantId) {
      return null;
    }

    const item = {
      id: Number(product.variantId),
      quantity: Math.max(1, Math.ceil(quantity || 1))
    };

    if (properties && Object.keys(properties).length) {
      item.properties = properties;
    }

    return item;
  }

  static createBundleCartItem(parentVariantId, components, options = {}) {
    const parsedParentVariantId = Number(parentVariantId);

    if (!Number.isFinite(parsedParentVariantId)) {
      return null;
    }

    const normalizedComponents = this.arrayify(components)
      .map((component) => {
        const parsedVariantId = Number(component && component.id);
        const parsedQuantity = Math.max(1, Math.ceil(Number(component && component.quantity) || 1));
        const parsedPrice = Number(component && component.price);

        if (!Number.isFinite(parsedVariantId)) {
          return null;
        }

        const normalizedComponent = {
          id: parsedVariantId,
          quantity: parsedQuantity
        };

        if (Number.isFinite(parsedPrice) && parsedPrice >= 0) {
          normalizedComponent.price = parsedPrice;
        }

        if (component && component.attributes && typeof component.attributes === 'object' && Object.keys(component.attributes).length) {
          normalizedComponent.attributes = component.attributes;
        }

        return normalizedComponent;
      })
      .filter(Boolean);

    if (!normalizedComponents.length) {
      return null;
    }

    const properties = {
      _components: JSON.stringify(normalizedComponents)
    };

    const usesComponentSumPricing = normalizedComponents.every((component) => Number.isFinite(component.price) && component.price > 0);
    const discount = this.positiveNumber(options.discountPercentage);

    if (discount && !usesComponentSumPricing) {
      properties._discount = discount;
    }

    Object.assign(properties, options.properties || {});

    const settings = {};

    if (options.settingsTitle) {
      settings.title = options.settingsTitle;
    }

    if (options.settingsImage) {
      settings.image = this.absoluteUrl(options.settingsImage);
    }

    if (Object.keys(settings).length) {
      properties._settings = JSON.stringify(settings);
    }

    return {
      id: parsedParentVariantId,
      quantity: Math.max(1, Math.ceil(Number(options.quantity) || 1)),
      properties
    };
  }

  static calculateRoofPlan(data, dimensions) {
    const profile = data.calculatorProfile || {};
    const rollWidths = this.uniqueNumbers(profile.availableRollWidthsM);
    const maxSingleWidth = this.positiveNumber(profile.maximumSingleSheetWidthM) || rollWidths[rollWidths.length - 1] || null;
    const maximumSheetLength = this.positiveNumber(profile.maximumSheetLengthM);
    const increment = this.positiveNumber(profile.roundingIncrementM) || 0.5;
    const perimeterStripWidth = this.positiveNumber(profile.perimeterStripWidthM) || 0.15;
    const rawFinishedLength = dimensions.length + dimensions.overhang;
    const rawFinishedWidth = dimensions.width + dimensions.overhang;
    const finishedLength = this.roundUp(rawFinishedLength, increment);
    const finishedWidth = this.roundUp(rawFinishedWidth, increment);
    const area = finishedLength * finishedWidth;
    const adhesiveBaseLength = dimensions.length;
    const adhesiveBaseWidth = dimensions.width;
    const perimeterRun = (adhesiveBaseLength * 2) + (adhesiveBaseWidth * 2);
    const innerLength = Math.max(adhesiveBaseLength - (perimeterStripWidth * 2), 0);
    const innerWidth = Math.max(adhesiveBaseWidth - (perimeterStripWidth * 2), 0);
    const fieldArea = innerLength * innerWidth;
    const perimeterArea = Math.max((adhesiveBaseLength * adhesiveBaseWidth) - fieldArea, 0);
    const exactMembrane = this.resolveExactMembrane(data.products && data.products.membranes, rollWidths, maxSingleWidth, maximumSheetLength, dimensions);
    const exceedsConfiguredLimits = !exactMembrane;
    const rollWidth = exactMembrane ? exactMembrane.width : this.findFirstWidthAtLeast(rollWidths, finishedWidth);

    return {
      area,
      finishedLength,
      finishedWidth,
      increment,
      fieldArea,
      perimeterArea,
      perimeterRun,
      perimeterStripWidth,
      rawFinishedLength,
      rawFinishedWidth,
      rawRoofLength: adhesiveBaseLength,
      rawRoofWidth: adhesiveBaseWidth,
      maxSingleWidth,
      maximumSheetLength,
      rollWidth,
      exactMembrane,
      exceedsConfiguredLimits
    };
  }

  static buildRecommendations(data, calculation, edgeSelections) {
    const products = data.products || {};
    const recommendations = new Map();
    const adhesiveRules = data.adhesiveAndSealantRules || [];
    const normalizedSelections = this.normalizeEdgeSelections(edgeSelections);
    const profile = data.calculatorProfile || {};
    const perimeterRule = this.findCoverageRule(adhesiveRules, (name, productType) => /perimeter/.test(name) || /perimeter/.test(productType), 3);
    const fieldCoverageFallback = this.positiveNumber(profile.fieldAdhesiveCoverage) || 3;
    const fieldRule = this.findCoverageRule(adhesiveRules, (name, productType) => /field/.test(name) || /field/.test(productType), fieldCoverageFallback);
    const perimeterQuantity = calculation.perimeterArea / perimeterRule.coverageValue;
    const fieldQuantity = calculation.fieldArea / fieldRule.coverageValue;

    if (calculation.exactMembrane) {
      const membraneMatch = calculation.exactMembrane;
      const membraneVariant = membraneMatch.variant;
      const membraneProduct = membraneVariant
        ? Object.assign({}, membraneMatch.product, {
          variantId: membraneVariant.id,
          price: membraneVariant.price,
          compareAtPrice: membraneVariant.compareAtPrice,
          available: membraneVariant.available
        })
        : membraneMatch.product;

      this.addProductRecommendation(
        recommendations,
        membraneProduct,
        `Core kit: membrane sized at ${this.formatMeters(membraneMatch.width)} x ${this.formatMeters(membraneMatch.length)}`,
        {
          quantity: 1
        }
      );
    }

    const perimeterOptions = this.buildAdhesiveOptions(products.perimeterAdhesiveOptions, products.perimeterBondingAdhesive);
    const fieldOptions = this.buildAdhesiveOptions(products.fieldAdhesiveOptions, products.fieldAdhesive);
    const perimeterCombination = this.selectAdhesivePackCombination(perimeterOptions, perimeterQuantity);
    const fieldCombination = this.selectAdhesivePackCombination(fieldOptions, fieldQuantity);

    if (perimeterCombination) {
      perimeterCombination.forEach(({ pack, quantity }) => {
        this.addProductRecommendation(
          recommendations,
          pack.product,
          `Core kit: perimeter bonding adhesive, approx. ${this.buildQuantityLabel(perimeterQuantity, 'L')}`,
          { quantity }
        );
      });
    } else {
      this.addProductRecommendation(
        recommendations,
        products.perimeterBondingAdhesive,
        `Core kit: perimeter bonding adhesive, approx. ${this.buildQuantityLabel(perimeterQuantity, 'L')}`,
        { quantity: this.resolvePackQuantity(perimeterQuantity, products.perimeterBondingAdhesive, perimeterRule.rule) }
      );
    }

    if (fieldCombination) {
      fieldCombination.forEach(({ pack, quantity }) => {
        this.addProductRecommendation(
          recommendations,
          pack.product,
          `Core kit: field bonding adhesive, approx. ${this.buildQuantityLabel(fieldQuantity, 'L')}`,
          { quantity }
        );
      });
    } else {
      this.addProductRecommendation(
        recommendations,
        products.fieldAdhesive,
        `Core kit: field bonding adhesive, approx. ${this.buildQuantityLabel(fieldQuantity, 'L')}`,
        { quantity: this.resolvePackQuantity(fieldQuantity, products.fieldAdhesive, fieldRule.rule) }
      );
    }

    ['drip', 'curb'].forEach((edgeType) => {
      const edgesForType = this.EDGE_POSITIONS.filter((edge) => normalizedSelections[edge] === edgeType);

      if (!edgesForType.length) {
        return;
      }

      const trimProduct = this.getTrimProduct(products, edgeType);
      const pieceLength = this.positiveNumber(trimProduct && trimProduct.pieceLengthM) || this.positiveNumber(this.parseWidthFromTitle(trimProduct && trimProduct.title)) || 1;
      const totalEdgeLength = edgesForType.reduce((total, edge) => {
        return total + this.edgeLengthMeters(edge, calculation.rawRoofLength, calculation.rawRoofWidth);
      }, 0);
      const trimQuantity = Math.ceil(totalEdgeLength / pieceLength);

      this.addProductRecommendation(
        recommendations,
        trimProduct,
        `Core kit: ${this.edgeTypeLabel(edgeType).toLowerCase()} for ${this.formatEdgeNames(edgesForType).toLowerCase()}`,
        {
          quantity: trimQuantity
        }
      );
    });

    this.arrayify(products.recommendedPrimers).forEach((product) => {
      this.addProductRecommendation(recommendations, product, 'Recommended accessory: primers', { quantity: 1 });
    });
    this.arrayify(products.recommendedSealants).forEach((product) => {
      this.addProductRecommendation(recommendations, product, 'Recommended accessory: sealants', { quantity: 1 });
    });
    this.arrayify(products.recommendedWaterBlockProducts).forEach((product) => {
      this.addProductRecommendation(recommendations, product, 'Recommended accessory: water block', { quantity: 1 });
    });
    this.arrayify(products.recommendedDrainageProducts).forEach((product) => {
      this.addProductRecommendation(recommendations, product, 'Recommended accessory: drainage outlets', { quantity: 1 });
    });
    this.arrayify(products.recommendedRoofLightAccessories).forEach((product) => {
      this.addProductRecommendation(recommendations, product, 'Recommended accessory: roof light accessories', { quantity: 1 });
    });
    this.arrayify(products.recommendedSeamProducts).forEach((product) => {
      this.addProductRecommendation(recommendations, product, 'Recommended accessory: seam tape', { quantity: 1 });
    });
    this.arrayify(products.recommendedFixings).forEach((product) => {
      this.addProductRecommendation(recommendations, product, 'Recommended accessory: fixings', { quantity: 1 });
    });

    return [...recommendations.values()];
  }

  static renderMessage(container, type, content) {
    const element = document.createElement('div');
    element.className = `epdm-calculator__message epdm-calculator__message--${type}`;
    element.textContent = content;
    container.appendChild(element);
  }

  static officeReviewMessage(profile) {
    return profile.largeRoofMessageBody
      || profile.contactOfficeMessage
      || 'Your roof size is outside our standard single-sheet limits, so our team will help you with the right setup.';
  }

  static createRecommendationImage(product) {
    if (!product || !product.image) {
      return null;
    }

    const image = document.createElement('img');
    image.src = product.image;
    image.alt = product.title || '';
    image.loading = 'lazy';
    image.decoding = 'async';
    return image;
  }

  static async addItemsToCart(items, openMiniCart, { showNotification = true } = {}) {
    const payload = {
      items
    };

    console.groupCollapsed('[EPDM Flex Bundle] /cart/add.js payload');
    console.log(payload);
    console.log('Copyable payload JSON:', JSON.stringify(payload, null, 2));
    console.table(items.map((item) => {
      let components = [];

      try {
        components = item && item.properties && item.properties._components
          ? JSON.parse(item.properties._components)
          : [];
      } catch (error) {
        components = [];
      }

      return {
        parentVariantId: item && item.id,
        quantity: item && item.quantity,
        componentCount: components.length,
        componentTotal: components.reduce((total, component) => total + ((Number(component.price) || 0) * (Math.max(1, Math.ceil(Number(component.quantity) || 1)))), 0),
        hasDiscount: Boolean(item && item.properties && item.properties._discount),
        hasComponents: Boolean(item && item.properties && item.properties._components)
      };
    }));
    console.groupEnd();

    const response = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const responseJson = await response.json();

    console.groupCollapsed('[EPDM Flex Bundle] /cart/add.js response');
    console.log(responseJson);
    console.table(RoofCalculator.arrayify(responseJson.items).map((item) => {
      let components = [];

      try {
        components = item && item.properties && item.properties._components
          ? JSON.parse(item.properties._components)
          : [];
      } catch (error) {
        components = [];
      }

      return {
        variantId: item && item.id,
        finalPrice: item && item.final_price,
        finalLinePrice: item && item.final_line_price,
        componentTotal: components.reduce((total, component) => total + ((Number(component.price) || 0) * (Math.max(1, Math.ceil(Number(component.quantity) || 1)))), 0),
        componentCount: components.length,
        componentPrices: components.map((component) => component.price).join(', '),
        hasDiscount: Boolean(item && item.properties && item.properties._discount)
      };
    }));
    console.groupEnd();

    if (!response.ok) {
      throw new Error(responseJson.description || 'Unable to add items to cart.');
    }

    const cartResponse = await fetch(`${window.themeVariables.routes.cartUrl}.js`);
    const cart = await cartResponse.json();

    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail: {
        cart
      }
    }));

    if (responseJson.sections) {
      cart.sections = responseJson.sections;
    } else {
      const miniCartSectionResponse = await fetch(`${window.themeVariables.routes.cartUrl}?section_id=mini-cart`);
      const miniCartSectionHtml = await miniCartSectionResponse.text();
      cart.sections = {
        'mini-cart': miniCartSectionHtml
      };
    }

    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: {
        cart,
        openMiniCart
      }
    }));

    if (showNotification) {
      document.documentElement.dispatchEvent(new CustomEvent('cart-notification:show', {
        bubbles: true,
        cancelable: true,
        detail: {
          status: 'success',
          error: ''
        }
      }));
    }
  }
}

class EpdmCalculator {
    constructor(root) {
      this.root = root;
      this.data = this.getData();

      if (!this.data) {
        return;
      }

      this.messages = this.root.querySelector('[data-epdm-messages]');
      this.breakdown = this.root.querySelector('[data-epdm-breakdown]');
      this.breakdownContent = this.root.querySelector('[data-epdm-breakdown-content]');
      this.productList = this.root.querySelector('[data-epdm-product-list]');
      this.addAllButton = this.root.querySelector('[data-epdm-add-all]');
      this.reviewButton = this.root.querySelector('[data-epdm-review-button]');
      this.accordion = this.root.querySelector('[data-epdm-accordion]');
      this.drawerSummary = this.root.querySelector('[data-epdm-drawer-summary]');
      this.drawerPriceValue = this.root.querySelector('[data-epdm-drawer-price-value]');
      this.drawerComparePrice = this.root.querySelector('[data-epdm-drawer-compare-price]');
      this.accessoryPopup = this.root.querySelector('[data-epdm-accessory-popup]');
      this.accessoryPopupGrid = this.root.querySelector('[data-epdm-accessory-popup-grid]');
      this.trimStep = this.root.querySelector('[data-epdm-step="trim"]');
      this.officeCallout = this.root.querySelector('[data-epdm-office-callout]');
      this.officeCalloutText = this.root.querySelector('[data-epdm-office-callout-text]');
      this.previewHint = this.root.querySelector('[data-epdm-preview-hint]');
      this.previewSize = this.root.querySelector('[data-epdm-preview-size]');
      this.previewDimensions = RoofCalculator.EDGE_POSITIONS.reduce((result, edge) => {
        result[edge] = this.root.querySelector(`[data-epdm-preview-dimension="${edge}"]`);
        return result;
      }, {});
      this.properties = {
        roofSize: this.root.querySelector('[data-epdm-property="roof_size"]'),
        membrane: this.root.querySelector('[data-epdm-property="membrane"]'),
        coverage: this.root.querySelector('[data-epdm-property="coverage"]'),
        sheetPlan: this.root.querySelector('[data-epdm-property="sheet_plan"]'),
        details: this.root.querySelector('[data-epdm-property="details"]')
      };
      this.form = this.properties.roofSize && this.properties.roofSize.form ? this.properties.roofSize.form : null;
      this.productForm = this.root.closest('.product-form');
      this.nativeVariantPicker = this.productForm && this.productForm.querySelector('.product-form__variants');
      this.nativeQuantitySelector = this.productForm && this.productForm.querySelector('.product-form__quantity');
      this.nativePaymentContainer = this.productForm && this.productForm.querySelector('.product-form__payment-container');
      this.paymentButtons = this.form ? Array.from(this.form.querySelectorAll('.shopify-payment-button')) : [];
      this.submitButtons = this.getSubmitButtons();
      this.priceElements = Array.from(document.querySelectorAll(`[data-epdm-price-value="${this.data.product.id}"]`));
      this.comparePriceElements = Array.from(document.querySelectorAll(`[data-epdm-compare-price-value="${this.data.product.id}"]`));
      this.basePrice = Number(this.data.product && this.data.product.price ? this.data.product.price : 0);
      this.baseCompareAtPrice = Number(this.data.product && this.data.product.compareAtPrice ? this.data.product.compareAtPrice : 0);
      this.isSubmitting = false;
      this.isAddingAccessory = false;

      this.bind();
      this.updateDisplayedPrice();
      this.update();
    }

    getData() {
      const container = this.root.closest('.product__info') || this.root.parentElement;
      const dataScript = container && container.querySelector('[data-epdm-builder-data]');

      if (!dataScript) {
        return null;
      }

      return RoofCalculator.parseJson(dataScript.textContent);
    }

    bind() {
      if (this.addAllButton) {
        this.addAllButton.addEventListener('click', () => {
          this.addAllRecommendedItems();
        });
      }

      if (this.form) {
        this.form.addEventListener('submit', (event) => {
          if (!this.shouldUseCalculatorPurchaseFlow()) {
            return;
          }

          event.preventDefault();
          this.addAllRecommendedItems();
        });
      }

      this.root.addEventListener('input', (event) => {
        this.update();
      });

      this.root.addEventListener('change', (event) => {
        this.update();
      });

      this.bindAccessoryPopup();

      if (this.reviewButton) {
        this.reviewButton.addEventListener('click', () => {
          this.update();

          if (this.reviewButton.disabled) {
            return;
          }

          this.openAccordion();
        });
      }
    }

    getSubmitButtons() {
      if (!this.form) {
        return [];
      }

      const buttons = new Set();

      this.form.querySelectorAll('[data-product-add-to-cart-button]').forEach((button) => {
        buttons.add(button);
      });

      document.querySelectorAll(`[form="${this.form.id}"][data-product-add-to-cart-button]`).forEach((button) => {
        buttons.add(button);
      });

      return [...buttons];
    }

    bindAccessoryPopup() {
      if (!this.accessoryPopup) {
        return;
      }

      this.accessoryPopup.querySelectorAll('[data-epdm-accessory-popup-close]').forEach((button) => {
        button.addEventListener('click', () => {
          this.closeAccessoryPopup();
        });
      });

      this.accessoryPopup.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.closeAccessoryPopup();
        }
      });

      if (this.accessoryPopupGrid) {
        this.accessoryPopupGrid.addEventListener('click', (event) => {
          const quantityButton = event.target.closest('[data-epdm-accessory-qty-action]');
          const addButton = event.target.closest('[data-epdm-accessory-add]');

          if (quantityButton) {
            this.adjustAccessoryQuantity(quantityButton);
          }

          if (addButton) {
            this.addAccessoryFromButton(addButton);
          }
        });
      }
    }

    calculateDisplayedPrice() {
      const priceSummary = (this.recommendations || []).reduce((totals, product) => {
        const quantity = Math.max(1, Math.ceil(Number(product && product.quantity) || 1));
        const unitPrice = Number(product && product.price ? product.price : 0);
        const unitCompareAtPrice = Number(product && product.compareAtPrice ? product.compareAtPrice : 0);

        totals.price += unitPrice * quantity;

        if (unitCompareAtPrice > unitPrice) {
          totals.compareAtPrice += unitCompareAtPrice * quantity;
        }

        return totals;
      }, { price: 0, compareAtPrice: 0 });

      if (priceSummary.price <= 0) {
        return {
          price: this.basePrice,
          compareAtPrice: this.baseCompareAtPrice > this.basePrice ? this.baseCompareAtPrice : 0
        };
      }

      const compareAtPrice = Math.max(
        priceSummary.price,
        priceSummary.compareAtPrice
      );

      return {
        price: priceSummary.price,
        compareAtPrice: compareAtPrice > priceSummary.price ? compareAtPrice : 0
      };
    }

    updateDisplayedPrice() {
      const priceSummary = this.calculateDisplayedPrice();
      const formattedPrice = RoofCalculator.formatMoneyValue(priceSummary.price);
      const formattedCompareAtPrice = priceSummary.compareAtPrice ? RoofCalculator.formatMoneyValue(priceSummary.compareAtPrice) : '';
      const hasCalculatedPrice = Array.isArray(this.recommendations) && this.recommendations.length > 0;
      const pricePrefix = hasCalculatedPrice ? '' : 'From ';

      this.priceElements.forEach((element) => {
        const hiddenLabel = element.querySelector('.visually-hidden');
        const placeholder = element.dataset.epdmPricePlaceholder;
        element.innerHTML = '';

        if (hiddenLabel) {
          element.appendChild(hiddenLabel);
        }

        element.appendChild(document.createTextNode(placeholder || `${pricePrefix}${formattedPrice}`));
      });

      this.comparePriceElements.forEach((element) => {
        const hiddenLabel = element.querySelector('.visually-hidden');

        if (!formattedCompareAtPrice) {
          element.hidden = true;
          return;
        }

        element.hidden = false;
        element.innerHTML = '';

        if (hiddenLabel) {
          element.appendChild(hiddenLabel);
        }

        element.appendChild(document.createTextNode(formattedCompareAtPrice));
      });

      if (this.drawerPriceValue) {
        this.drawerPriceValue.textContent = formattedPrice;
      }

      if (this.drawerComparePrice) {
        if (!formattedCompareAtPrice) {
          this.drawerComparePrice.hidden = true;
          this.drawerComparePrice.textContent = '';
        } else {
          this.drawerComparePrice.hidden = false;
          this.drawerComparePrice.textContent = formattedCompareAtPrice;
        }
      }
    }

    shouldUseCalculatorPurchaseFlow() {
      const hasDimensions = Object.values(this.properties).some((input) => input && input.value);
      return hasDimensions && Array.isArray(this.recommendations) && this.recommendations.length > 0;
    }

    syncNativePurchaseUI() {
      const useCalculatorPurchaseFlow = this.shouldUseCalculatorPurchaseFlow();

      if (this.nativeVariantPicker) {
        this.nativeVariantPicker.hidden = useCalculatorPurchaseFlow;
      }

      if (this.nativeQuantitySelector) {
        this.nativeQuantitySelector.hidden = useCalculatorPurchaseFlow;
      }

      if (this.nativePaymentContainer) {
        this.nativePaymentContainer.hidden = useCalculatorPurchaseFlow;
      }

      this.paymentButtons.forEach((button) => {
        button.hidden = useCalculatorPurchaseFlow;
      });

      this.submitButtons.forEach((button) => {
        if (!button.dataset.epdmOriginalLabel) {
          button.dataset.epdmOriginalLabel = button.textContent.trim();
        }

        button.hidden = useCalculatorPurchaseFlow;
        button.textContent = button.dataset.epdmOriginalLabel;
      });

      if (this.addAllButton) {
        this.addAllButton.hidden = !useCalculatorPurchaseFlow;
      }
    }

    setAccordionState(isOpen) {
      if (!this.accordion) {
        return;
      }

      this.accordion.hidden = !isOpen;

      if (this.reviewButton) {
        this.reviewButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }
    }

    openAccordion() {
      if (!this.accordion || this.reviewButton?.disabled) {
        return;
      }

      this.setAccordionState(true);
      this.accordion.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    getInput(name) {
      return this.root.querySelector(`[data-epdm-input="${name}"]`);
    }

    getInputNumber(name) {
      const input = this.getInput(name);
      return RoofCalculator.positiveNumber(input && input.value);
    }

    getEdgeSelections() {
      return RoofCalculator.EDGE_POSITIONS.reduce((result, edge) => {
        const input = this.getInput(`edge-${edge}`);
        result[edge] = RoofCalculator.normalizeText(input && input.value) || 'none';
        return result;
      }, {});
    }

    clearResults() {
      if (this.messages) {
        this.messages.innerHTML = '';
      }

      if (this.breakdown) {
        this.breakdown.hidden = true;
      }

      if (this.breakdownContent) {
        this.breakdownContent.textContent = '';
      }

      if (this.productList) {
        this.productList.innerHTML = '';
      }
    }

    resetActionButtons() {
      if (this.addAllButton) {
        this.addAllButton.hidden = true;
        this.addAllButton.removeAttribute('disabled');
        this.addAllButton.removeAttribute('aria-busy');
      }

      this.setReviewButtonState({
        hidden: false,
        disabled: true,
        label: 'Calculate'
      });
    }

    setReviewButtonState({ hidden = false, disabled = false, label = 'Calculate' } = {}) {
      if (!this.reviewButton) {
        return;
      }

      this.reviewButton.hidden = hidden;
      this.reviewButton.disabled = disabled;
      this.reviewButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      this.reviewButton.textContent = label;
    }

    resetDrawerSummary(message = 'Your coverage area and kit products will appear here.') {
      if (this.drawerSummary) {
        this.drawerSummary.textContent = message;
      }
    }

    renderCalculatorMessage(type, content) {
      if (this.messages) {
        RoofCalculator.renderMessage(this.messages, type, content);
      }
    }

    buildCalculationViewModel(length, width, calculation, edgeSelections, overhang) {
      const membraneLabel = calculation.exactMembrane
        ? `${RoofCalculator.formatMeters(calculation.exactMembrane.width)} x ${RoofCalculator.formatMeters(calculation.exactMembrane.length)}`
        : `${RoofCalculator.formatMeters(calculation.rollWidth)} x ${RoofCalculator.formatMeters(calculation.finishedLength)}`;

      return {
        roofSizeLabel: `${RoofCalculator.formatMeters(length)} x ${RoofCalculator.formatMeters(width)}`,
        finishedSizeLabel: `${RoofCalculator.formatMeters(calculation.finishedLength)} x ${RoofCalculator.formatMeters(calculation.finishedWidth)}`,
        edgeSelectionSummary: RoofCalculator.buildEdgeSelectionSummary(edgeSelections),
        sheetPlanLabel: `Single sheet at ${membraneLabel}`,
        overhangTotal: overhang,
        overhangEachSide: overhang / 2
      };
    }

    buildBreakdownText(length, width, calculation, viewModel, overhang) {
      return [
        `Input size: ${RoofCalculator.formatMeters(length)} x ${RoofCalculator.formatMeters(width)}`,
        `Overhang: +${RoofCalculator.formatMeters(viewModel.overhangEachSide)} each side (adds ${RoofCalculator.formatMeters(viewModel.overhangTotal)} total per dimension)`,
        `After overhang: ${RoofCalculator.formatMeters(calculation.rawFinishedLength)} x ${RoofCalculator.formatMeters(calculation.rawFinishedWidth)}`,
        `Rounded to ${RoofCalculator.trimTrailingZeros(calculation.increment)}m: ${RoofCalculator.formatMeters(calculation.finishedLength)} x ${RoofCalculator.formatMeters(calculation.finishedWidth)}`,
        `Area: ${RoofCalculator.formatSquareMeters(calculation.area)}`,
        `Selected roll width: ${RoofCalculator.formatMeters(calculation.rollWidth)}`,
        `Sheet plan: ${viewModel.sheetPlanLabel}`
      ].join('\n');
    }

    renderRecommendations(recommendations) {
      if (!this.productList) {
        return;
      }

      recommendations.forEach((product) => {
        const item = document.createElement('li');
        const card = document.createElement('div');
        card.className = 'epdm-calculator__product';

        const mediaWrapper = product.url ? document.createElement('a') : document.createElement('div');
        mediaWrapper.className = 'epdm-calculator__product-media';

        if (product.url) {
          mediaWrapper.href = product.url;
          mediaWrapper.setAttribute('aria-label', product.title);
        }

        const productImage = RoofCalculator.createRecommendationImage(product);

        if (productImage) {
          mediaWrapper.appendChild(productImage);
        }

        const content = document.createElement('div');
        content.className = 'epdm-calculator__product-content';

        const titleNode = product.url ? document.createElement('a') : document.createElement('span');
        titleNode.className = 'epdm-calculator__product-title';
        titleNode.textContent = product.title;

        if (product.url) {
          titleNode.href = product.url;
        }

        content.appendChild(titleNode);

        if (product.reason) {
          const reasonNode = document.createElement('p');
          reasonNode.className = 'epdm-calculator__product-reason text--small text--subdued';
          const quantityPrefix = product.quantity && product.quantity > 1 ? `${product.quantity} x ` : '';
          reasonNode.textContent = `${quantityPrefix}${product.reason}`;
          content.appendChild(reasonNode);
        }

        card.appendChild(mediaWrapper);
        card.appendChild(content);
        item.appendChild(card);
        this.productList.appendChild(item);
      });
    }

    getAccessoryProducts() {
      const products = this.data.products || {};
      return RoofCalculator.arrayify(products.recommendedProducts)
        .map((product) => RoofCalculator.normalizeProductRef(product, 'Recommended accessory', { quantity: 1 }))
        .filter((product) => product && product.variantId && product.available !== false)
        .slice(0, 4);
    }

    renderAccessoryPopup() {
      if (!this.accessoryPopupGrid) {
        return;
      }

      const accessories = this.getAccessoryProducts();
      this.accessoryPopupGrid.innerHTML = '';

      accessories.forEach((product) => {
        const card = document.createElement('article');
        card.className = 'epdm-accessory-popup__product';

        const media = product.url ? document.createElement('a') : document.createElement('div');
        media.className = 'epdm-accessory-popup__media';

        if (product.url) {
          media.href = product.url;
          media.setAttribute('aria-label', product.title);
        }

        const image = RoofCalculator.createRecommendationImage(product);

        if (image) {
          media.appendChild(image);
        }

        const title = document.createElement('a');
        title.className = 'epdm-accessory-popup__title';
        title.textContent = product.title;
        title.href = product.url || '#';

        if (!product.url) {
          title.removeAttribute('href');
        }

        const price = document.createElement('p');
        price.className = 'epdm-accessory-popup__price text--small text--subdued';
        price.textContent = RoofCalculator.formatMoneyValue(product.price);

        const controls = document.createElement('div');
        controls.className = 'epdm-accessory-popup__controls';

        const quantity = document.createElement('div');
        quantity.className = 'epdm-accessory-popup__quantity quantity-selector quantity-selector--small';
        quantity.innerHTML = `
          <button type="button" class="quantity-selector__button" data-epdm-accessory-qty-action="decrease" aria-label="Decrease quantity">-</button>
          <input class="quantity-selector__input text--xsmall" type="number" inputmode="numeric" min="1" value="1" aria-label="Quantity" data-epdm-accessory-quantity>
          <button type="button" class="quantity-selector__button" data-epdm-accessory-qty-action="increase" aria-label="Increase quantity">+</button>
        `;

        const addButton = document.createElement('button');
        addButton.type = 'button';
        addButton.className = 'epdm-accessory-popup__add button button--secondary';
        addButton.textContent = 'Add to cart';
        addButton.dataset.epdmAccessoryAdd = String(product.variantId);

        controls.appendChild(quantity);
        controls.appendChild(addButton);
        card.appendChild(media);
        card.appendChild(title);
        card.appendChild(price);
        card.appendChild(controls);
        this.accessoryPopupGrid.appendChild(card);
      });
    }

    showAccessoryPopup() {
      const accessories = this.getAccessoryProducts();

      if (!this.accessoryPopup || !accessories.length) {
        return false;
      }

      this.renderAccessoryPopup();
      this.accessoryPopup.hidden = false;
      document.documentElement.classList.add('epdm-accessory-popup-is-open');
      this.accessoryPopup.focus();
      return true;
    }

    closeAccessoryPopup() {
      if (!this.accessoryPopup) {
        return;
      }

      this.accessoryPopup.hidden = true;
      document.documentElement.classList.remove('epdm-accessory-popup-is-open');
    }

    adjustAccessoryQuantity(button) {
      const card = button.closest('.epdm-accessory-popup__product');
      const input = card && card.querySelector('[data-epdm-accessory-quantity]');

      if (!input) {
        return;
      }

      const currentValue = Math.max(1, Math.ceil(Number(input.value) || 1));
      const nextValue = button.dataset.epdmAccessoryQtyAction === 'increase'
        ? currentValue + 1
        : Math.max(1, currentValue - 1);

      input.value = String(nextValue);
    }

    async addAccessoryFromButton(button) {
      if (this.isAddingAccessory) {
        return;
      }

      const card = button.closest('.epdm-accessory-popup__product');
      const input = card && card.querySelector('[data-epdm-accessory-quantity]');
      const variantId = Number(button.dataset.epdmAccessoryAdd);
      const quantity = Math.max(1, Math.ceil(Number(input && input.value) || 1));

      if (!Number.isFinite(variantId)) {
        return;
      }

      this.isAddingAccessory = true;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');

      try {
        await RoofCalculator.addItemsToCart([{ id: variantId, quantity }], false, { showNotification: false });
        card.classList.add('is-added');
        card.querySelectorAll('[data-epdm-accessory-qty-action], [data-epdm-accessory-quantity]').forEach((control) => {
          control.disabled = true;
        });
        button.textContent = 'Added';
      } catch (error) {
        button.textContent = 'Try again';
        button.disabled = false;
        this.renderCalculatorMessage('warning', error.message || 'Unable to add this accessory right now.');
      } finally {
        this.isAddingAccessory = false;
        button.removeAttribute('aria-busy');
      }
    }

    syncCalculatedProperties(calculation, viewModel) {
      if (this.properties.roofSize) {
        this.properties.roofSize.value = viewModel.roofSizeLabel;
      }

      if (this.properties.membrane) {
        this.properties.membrane.value = calculation.exactMembrane
          ? calculation.exactMembrane.product.title
          : viewModel.sheetPlanLabel;
      }

      if (this.properties.coverage) {
        this.properties.coverage.value = RoofCalculator.formatSquareMeters(calculation.area);
      }

      if (this.properties.sheetPlan) {
        this.properties.sheetPlan.value = viewModel.sheetPlanLabel;
      }

      if (this.properties.details) {
        this.properties.details.value = viewModel.edgeSelectionSummary;
      }
    }

    updateTrimStep(length, width, edgeSelections = {}, isAvailable = false) {
      const hasDimensions = Boolean(length && width);
      const showTrimStep = hasDimensions && isAvailable;
      const normalizedSelections = RoofCalculator.normalizeEdgeSelections(edgeSelections);
      const previewBorderColor = '#288d3f';

      if (this.trimStep) {
        this.trimStep.hidden = !showTrimStep;
        this.trimStep.setAttribute('aria-disabled', showTrimStep ? 'false' : 'true');
        this.trimStep.querySelectorAll('select').forEach((input) => {
          input.disabled = !showTrimStep;
        });
      }

      if (this.previewHint) {
        this.previewHint.hidden = hasDimensions;
      }

      if (this.previewSize) {
        this.previewSize.textContent = hasDimensions
          ? `${RoofCalculator.formatMeters(width)} x ${RoofCalculator.formatMeters(length)}`
          : '- x -';
      }

      RoofCalculator.EDGE_POSITIONS.forEach((edge) => {
        const element = this.previewDimensions && this.previewDimensions[edge];

        if (!element) {
          return;
        }

        const dimensionValue = edge === 'top' || edge === 'bottom' ? width : length;
        element.textContent = `${RoofCalculator.EDGE_LABELS[edge]}: ${hasDimensions ? RoofCalculator.formatMeters(dimensionValue) : '-'}`;
      });

      if (hasDimensions) {
        const longestSide = Math.max(length, width);
        const normalizedWidth = Math.max(0.38, Math.min(0.8, width / longestSide));
        const normalizedLength = Math.max(0.38, Math.min(0.84, length / longestSide));

        this.root.style.setProperty('--epdm-preview-shape-width', `${(normalizedWidth * 100).toFixed(2)}%`);
        this.root.style.setProperty('--epdm-preview-shape-height', `${(normalizedLength * 100).toFixed(2)}%`);
      } else {
        this.root.style.setProperty('--epdm-preview-shape-width', '68%');
        this.root.style.setProperty('--epdm-preview-shape-height', '78%');
      }

      this.root.style.setProperty('--epdm-preview-border-top', RoofCalculator.edgeSelectionBorderColor(hasDimensions ? normalizedSelections.top : 'none', previewBorderColor));
      this.root.style.setProperty('--epdm-preview-border-right', RoofCalculator.edgeSelectionBorderColor(hasDimensions ? normalizedSelections.right : 'none', previewBorderColor));
      this.root.style.setProperty('--epdm-preview-border-bottom', RoofCalculator.edgeSelectionBorderColor(hasDimensions ? normalizedSelections.bottom : 'none', previewBorderColor));
      this.root.style.setProperty('--epdm-preview-border-left', RoofCalculator.edgeSelectionBorderColor(hasDimensions ? normalizedSelections.left : 'none', previewBorderColor));
      this.root.style.setProperty('--epdm-preview-border-top-style', RoofCalculator.edgeSelectionBorderStyle(hasDimensions ? normalizedSelections.top : 'none'));
      this.root.style.setProperty('--epdm-preview-border-right-style', RoofCalculator.edgeSelectionBorderStyle(hasDimensions ? normalizedSelections.right : 'none'));
      this.root.style.setProperty('--epdm-preview-border-bottom-style', RoofCalculator.edgeSelectionBorderStyle(hasDimensions ? normalizedSelections.bottom : 'none'));
      this.root.style.setProperty('--epdm-preview-border-left-style', RoofCalculator.edgeSelectionBorderStyle(hasDimensions ? normalizedSelections.left : 'none'));
    }

    update({ preserveAccordion = false } = {}) {
      const shouldKeepAccordionOpen = preserveAccordion && this.accordion && !this.accordion.hidden;
      const length = this.getInputNumber('length');
      const width = this.getInputNumber('width');
      const edgeSelections = this.getEdgeSelections();
      const profile = this.data.calculatorProfile || {};
      const overhang = RoofCalculator.resolveOverhang(profile);

      this.toggleOfficeCallout(false);
      this.updateTrimStep(length, width, edgeSelections, false);
      this.clearResults();
      this.resetActionButtons();

      if (!shouldKeepAccordionOpen) {
        this.setAccordionState(false);
      }

      if (!length || !width) {
        this.clearProperties();
        this.recommendations = [];
        this.bundleRecommendations = [];
        this.resetDrawerSummary();
        this.updateDisplayedPrice();
        this.syncNativePurchaseUI();
        this.renderCalculatorMessage('info', 'Enter the roof length and width to calculate the membrane size.');
        return;
      }

      const calculation = RoofCalculator.calculateRoofPlan(this.data, { length, width, overhang });
      this.updateTrimStep(length, width, edgeSelections, !calculation.exceedsConfiguredLimits);
      const viewModel = this.buildCalculationViewModel(length, width, calculation, edgeSelections, overhang);

      this.renderCalculatorMessage('info', `Calculated roof coverage: ${viewModel.finishedSizeLabel} (${RoofCalculator.formatSquareMeters(calculation.area)}), including ${RoofCalculator.formatMeters(viewModel.overhangEachSide)} overhang on each side.`);
      this.renderCalculatorMessage('info', `Selected edge trims: ${viewModel.edgeSelectionSummary}.`);

      if (width > RoofCalculator.EPDM_SUPPORT_WIDTH_LIMIT_M) {
        this.renderCalculatorMessage('warning', `Roofs above ${RoofCalculator.EPDM_SUPPORT_WIDTH_LIMIT_M}m wide need a manual review. Please contact support before ordering.`);
      }

      if (calculation.exceedsConfiguredLimits) {
        const largeRoofMessage = RoofCalculator.officeReviewMessage(profile);
        this.toggleOfficeCallout(true, largeRoofMessage);
        this.renderCalculatorMessage('warning', largeRoofMessage);
        this.clearProperties();
        this.recommendations = [];
        this.bundleRecommendations = [];
        this.setReviewButtonState({ hidden: true, disabled: true });
        this.resetDrawerSummary('This roof size needs a manual review from our office.');
        this.updateDisplayedPrice();
        this.syncNativePurchaseUI();
        return;
      }

      if (this.breakdown && this.breakdownContent) {
        this.breakdownContent.textContent = this.buildBreakdownText(length, width, calculation, viewModel, overhang);
        this.breakdown.hidden = false;
      }

      const bundleRecommendations = RoofCalculator.buildRecommendations(this.data, calculation, edgeSelections);
      const recommendations = bundleRecommendations;
      this.renderRecommendations(recommendations);
      this.recommendations = recommendations.filter((product) => product.variantId && product.available !== false);
      this.bundleRecommendations = bundleRecommendations.filter((product) => product.variantId && product.available !== false);
      this.setReviewButtonState({
        hidden: false,
        disabled: false,
        label: this.recommendations.length > 0 ? `Calculate kit (${this.recommendations.length})` : 'Calculate kit'
      });
      this.resetDrawerSummary(`${RoofCalculator.formatSquareMeters(calculation.area)} coverage area with ${recommendations.length} kit item${recommendations.length === 1 ? '' : 's'}.`);

      this.updateDisplayedPrice();
      this.syncNativePurchaseUI();
      this.syncCalculatedProperties(calculation, viewModel);

      if (shouldKeepAccordionOpen) {
        this.setAccordionState(true);
      }
    }

    toggleOfficeCallout(visible, message = '') {
      if (!this.officeCallout) {
        return;
      }

      this.officeCallout.hidden = !visible;

      if (visible && this.officeCalloutText) {
        this.officeCalloutText.textContent = message;
      }
    }

    clearProperties() {
      Object.values(this.properties).forEach((input) => {
        if (!input) {
          return;
        }

        input.value = '';
      });
    }

    setAddAllButtonBusy(isBusy) {
      if (!this.addAllButton) {
        return;
      }

      if (isBusy) {
        this.addAllButton.setAttribute('disabled', 'disabled');
        this.addAllButton.setAttribute('aria-busy', 'true');
      } else {
        this.addAllButton.removeAttribute('disabled');
        this.addAllButton.removeAttribute('aria-busy');
      }
    }

    getLineItemProperties() {
      const lineItemProperties = {};

      Object.values(this.properties).forEach((input) => {
        if (!input || !input.name || !input.value) {
          return;
        }

        const match = input.name.match(/^properties\[(.+)\]$/);

        if (match) {
          lineItemProperties[match[1]] = input.value;
        }
      });

      (this.bundleRecommendations || this.recommendations || []).forEach((product, index) => {
        lineItemProperties[`Item ${index + 1}`] = `${product.quantity > 1 ? `${product.quantity} x ` : ''}${product.title}`;
      });

      return lineItemProperties;
    }

    buildCartItems(lineItemProperties) {
      const fallbackItems = [];
      const bundleComponents = [];
      let componentTotal = 0;
      let hasMissingComponentPrice = false;

      const bundleProducts = this.bundleRecommendations || this.recommendations || [];

      bundleProducts.forEach((product, index) => {
        const componentVariantId = Number(product && product.variantId);
        const componentQuantity = Math.max(1, Math.ceil(Number(product && product.quantity) || 1));

        const componentPrice = Number((Number(product && product.price ? product.price : 0) / 100).toFixed(2));

        if (Number.isFinite(componentVariantId)) {
          if (!Number.isFinite(componentPrice) || componentPrice <= 0) {
            hasMissingComponentPrice = true;
          } else {
            componentTotal += componentPrice * componentQuantity;
          }

          bundleComponents.push({
            id: componentVariantId,
            quantity: componentQuantity,
            // Sum option expects component prices in major currency units.
            price: componentPrice
          });
        }

        const item = RoofCalculator.createCartItem(product, product.quantity, index === 0 ? lineItemProperties : null);

        if (item) {
          fallbackItems.push(item);
        }
      });

      const bundleConfig = this.data && this.data.bundle ? this.data.bundle : {};
      const formVariantInput = this.form ? this.form.querySelector('input[name="id"]') : null;
      const parentVariantId = bundleConfig.parentVariantId || (formVariantInput ? formVariantInput.value : null);
      if (hasMissingComponentPrice || componentTotal <= 0) {
        return fallbackItems;
      }

      const bundleProperties = Object.assign({}, lineItemProperties);
      const bundleParentItem = RoofCalculator.createBundleCartItem(parentVariantId, bundleComponents, {
        quantity: 1,
        settingsTitle: bundleConfig.settingsTitle || (this.data && this.data.product ? this.data.product.title : ''),
        settingsImage: bundleConfig.settingsImage || (this.data && this.data.product ? this.data.product.image : ''),
        properties: bundleProperties
      });

      return bundleParentItem ? [bundleParentItem] : fallbackItems;
    }

    async addAllRecommendedItems() {
      if (this.isSubmitting) {
        return;
      }

      const lineItemProperties = this.getLineItemProperties();
      const items = this.buildCartItems(lineItemProperties);

      if (!items.length) {
        this.renderCalculatorMessage('warning', 'There are no calculator items ready to add yet.');
        return;
      }

      this.setAddAllButtonBusy(true);
      this.isSubmitting = true;

      try {
        await RoofCalculator.addItemsToCart(items, false, { showNotification: false });

        if (!this.showAccessoryPopup()) {
          document.documentElement.dispatchEvent(new CustomEvent('cart-notification:show', {
            bubbles: true,
            cancelable: true,
            detail: {
              status: 'success',
              error: ''
            }
          }));
        }
      } catch (error) {
        this.renderCalculatorMessage('warning', error.message || 'Unable to add the recommended items right now.');
        document.documentElement.dispatchEvent(new CustomEvent('cart-notification:show', {
          bubbles: true,
          cancelable: true,
          detail: {
            status: 'error',
            error: error.message || ''
          }
        }));
      } finally {
        this.isSubmitting = false;
        this.setAddAllButtonBusy(false);
      }
    }
}

RoofCalculator.CALCULATOR_SELECTOR = '[data-epdm-calculator]';
RoofCalculator.EPDM_SUPPORT_WIDTH_LIMIT_M = 7;

RoofCalculator.initCalculators = function initCalculators(context = document) {
  const calculators = context.matches && context.matches(RoofCalculator.CALCULATOR_SELECTOR)
      ? [context]
      : Array.from(context.querySelectorAll(RoofCalculator.CALCULATOR_SELECTOR));

  calculators.forEach((calculator) => {
    if (calculator.dataset.epdmInitialized === 'true') {
      return;
    }

    calculator.dataset.epdmInitialized = 'true';
    new EpdmCalculator(calculator);
  });
};

RoofCalculator.boot = function boot() {
  RoofCalculator.initCalculators(document);

  if (!document.body) {
    return;
  }

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) {
          return;
        }

        RoofCalculator.initCalculators(node);
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', RoofCalculator.boot);
} else {
  RoofCalculator.boot();
}
